import type { StaffMember, UserRole } from '../types';

/** Display order for roster role sections (house preference). */
export const ROSTER_ROLE_CATEGORY_ORDER = [
  'Manager',
  'Chef',
  'Kitchenhand',
  'Front Staff',
  'Dishwasher',
] as const;

export type RosterRoleCategory = (typeof ROSTER_ROLE_CATEGORY_ORDER)[number] | 'Other';

function normalizeRoleKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

/**
 * Map staff role id/name to a roster category.
 * Waitstaff / Cashier and similar FOH roles group under Front Staff.
 */
export function rosterRoleCategory(
  roleId: string,
  roleName?: string | null
): RosterRoleCategory {
  const id = normalizeRoleKey(roleId);
  const name = normalizeRoleKey(roleName ?? '');

  const match = (keys: string[]) => keys.some((k) => id === k || name === k);

  if (match(['manager'])) return 'Manager';
  if (match(['chef', 'headchef', 'souschef'])) return 'Chef';
  if (match(['kitchenhand', 'kitchenassistant'])) return 'Kitchenhand';
  if (
    match([
      'frontstaff',
      'frontofhouse',
      'foh',
      'waitstaff',
      'waiter',
      'waitress',
      'server',
      'cashier',
      'host',
      'hostess',
    ])
  ) {
    return 'Front Staff';
  }
  if (match(['dishwasher', 'dishhand', 'kp'])) return 'Dishwasher';

  return 'Other';
}

export function rosterRoleCategoryRank(category: RosterRoleCategory): number {
  const idx = (ROSTER_ROLE_CATEGORY_ORDER as readonly string[]).indexOf(category);
  return idx >= 0 ? idx : ROSTER_ROLE_CATEGORY_ORDER.length;
}

export function resolveStaffRoleName(
  member: Pick<StaffMember, 'role'>,
  userRoles: UserRole[]
): string {
  return userRoles.find((r) => r.id === member.role)?.name ?? member.role;
}

export function compareStaffForRoster(
  a: Pick<StaffMember, 'name' | 'role'>,
  b: Pick<StaffMember, 'name' | 'role'>,
  userRoles: UserRole[]
): number {
  const catA = rosterRoleCategory(a.role, resolveStaffRoleName(a, userRoles));
  const catB = rosterRoleCategory(b.role, resolveStaffRoleName(b, userRoles));
  const rank = rosterRoleCategoryRank(catA) - rosterRoleCategoryRank(catB);
  if (rank !== 0) return rank;
  if (catA === 'Other' && catB === 'Other') {
    const nameA = resolveStaffRoleName(a, userRoles);
    const nameB = resolveStaffRoleName(b, userRoles);
    const byRole = nameA.localeCompare(nameB);
    if (byRole !== 0) return byRole;
  }
  return a.name.localeCompare(b.name);
}

export interface RosterRoleGroup<T extends Pick<StaffMember, 'name' | 'role'>> {
  category: RosterRoleCategory;
  /** Section heading; for Other uses the role display name when all share one, else "Other". */
  label: string;
  members: T[];
}

/** Group staff into ordered role sections for the roster grid / export. */
export function groupStaffByRosterRole<T extends Pick<StaffMember, 'name' | 'role'>>(
  staff: T[],
  userRoles: UserRole[]
): RosterRoleGroup<T>[] {
  const sorted = staff.slice().sort((a, b) => compareStaffForRoster(a, b, userRoles));
  const groups: RosterRoleGroup<T>[] = [];

  for (const member of sorted) {
    const category = rosterRoleCategory(member.role, resolveStaffRoleName(member, userRoles));
    const roleLabel =
      category === 'Other' ? resolveStaffRoleName(member, userRoles) : category;
    const last = groups[groups.length - 1];
    if (last && last.category === category && (category !== 'Other' || last.label === roleLabel)) {
      last.members.push(member);
    } else {
      groups.push({ category, label: roleLabel, members: [member] });
    }
  }

  return groups;
}
